// Real pixel-art assets (SunnyLand pack, see /Sunny-land-files in the repo
// root and public-license.pdf for the license) — copied into public/assets
// so they're served as static files and loaded here via the browser's Image
// element (not bundled through esbuild, which has no image loader wired up).
//
// Everything is exposed as small "AnimSet" descriptors (frame URLs + native
// pixel size) so GameView.tsx can pick a frame index and drawImage() it into
// the target world-space box without caring about each source PNG's size.

export type AnimSet = { frames: HTMLImageElement[]; nativeWidth: number; nativeHeight: number };

const imageCache = new Map<string, HTMLImageElement>();
function loadImage(src: string): HTMLImageElement {
	let img = imageCache.get(src);
	if (!img) {
		img = new Image();
		img.src = src;
		imageCache.set(src, img);
	}
	return img;
}

function anim(paths: string[], nativeWidth: number, nativeHeight: number): AnimSet {
	return { frames: paths.map(loadImage), nativeWidth, nativeHeight };
}

const P = "/assets/sunnyland";

// Foxy — the player character. Every animation's frames are a consistent
// 33x32 native size (see Sunny-land-files/.../Characters/Foxy/*/sprites).
export const PLAYER = {
	IDLE: anim([1, 2, 3, 4].map((n) => `${P}/player/idle/f-0${n}.png`), 33, 32),
	RUN: anim([1, 2, 3, 4, 5, 6].map((n) => `${P}/player/run/f-0${n}.png`), 33, 32),
	JUMP: anim([1, 2].map((n) => `${P}/player/jump/f-0${n}.png`), 33, 32),
	HURT: anim([1, 2].map((n) => `${P}/player/hurt/f-0${n}.png`), 33, 32),
	VICTORY: anim([`${P}/player/victory/f-01.png`], 33, 32),
};

// Opossum — ground patrol enemy (replaces the old procedural "crab"). Native
// frame size 36x28.
export const OPOSSUM = anim([1, 2, 3, 4, 5, 6].map((n) => `${P}/opossum/opossum-${n}.png`), 36, 28);

// Eagle attack swoop — flying patrol enemy (replaces the old procedural
// "seagull"). Native frame size 40x41.
export const EAGLE = anim([1, 2, 3, 4].map((n) => `${P}/eagle/eagle-attack-${n}.png`), 40, 41);

// Ground / platform tiles — single 16x16 (or 32x32) props tiled by GameView.
export const TILE_GROUND = loadImage(`${P}/tiles/ground.png`); // 16x16
export const TILE_PLATFORM = loadImage(`${P}/tiles/platform.png`); // 16x16

// Collectible items — gem (purple, 15x13) and cherry (21x21).
export const ITEM_GEM = anim([1, 2, 3, 4, 5].map((n) => `${P}/items/gem/gem-${n}.png`), 15, 13);
export const ITEM_CHERRY = anim([1, 2, 3, 4, 5, 6, 7].map((n) => `${P}/items/cherry/cherry-${n}.png`), 21, 21);

// Power-up pickup — reuse the sparkle FX as an idle pulse (32x32).
export const POWERUP_PULSE = anim(
	[1, 2, 3, 4].map((n) => `${P}/fx/item-feedback/item-feedback-${n}.png`),
	32,
	32,
);

// Enemy stomp/death poof (40x41).
export const ENEMY_DEATH = anim([1, 2, 3, 4, 5, 6].map((n) => `${P}/fx/enemy-death/enemy-death-${n}.png`), 40, 41);

// Parallax background layers — a full sky+cloud+ocean band (384x240) and a
// distant jungle-hill silhouette (176x368), both tiled horizontally.
export const BG_SKY = loadImage(`${P}/bg/back.png`);
export const BG_HILL = loadImage(`${P}/bg/middle.png`);

// Landmark set-piece art — one static prop per LandmarkKind. Not literal
// depictions of the named Visakhapatnam landmark, but consistent SunnyLand
// pixel art standing in for it (nicer than the old procedural silhouette).
export const LANDMARK_PROPS: Record<string, HTMLImageElement> = {
	hill: loadImage(`${P}/props/rock-2.png`),
	lighthouse: loadImage(`${P}/props/pine.png`),
	temple: loadImage(`${P}/props/wooden-house.png`),
	submarine: loadImage(`${P}/props/rock-1.png`),
	statue: loadImage(`${P}/props/sign.png`),
	dome: loadImage(`${P}/props/house.png`),
	pier: loadImage(`${P}/props/platform-long.png`),
	caves: loadImage(`${P}/props/rock-1.png`),
	ferris: loadImage(`${P}/props/tree-house.png`),
	bridge: loadImage(`${P}/props/platform-long.png`),
	aircraft: loadImage(`${P}/props/big-crate.png`),
	port: loadImage(`${P}/props/crate.png`),
};

// Decorative palm/pine/tree/bush/rock props scattered along the ground for
// visual variety — drawn behind the ground line, picked deterministically per
// world-x by GameView so they don't shift frame to frame.
export const GROUND_PROPS: HTMLImageElement[] = [
	loadImage(`${P}/props/palm.png`),
	loadImage(`${P}/props/pine.png`),
	loadImage(`${P}/props/tree.png`),
	loadImage(`${P}/props/bush.png`),
	loadImage(`${P}/props/rock.png`),
];

export function animFrame(set: AnimSet, nowMs: number, frameDurationMs: number): HTMLImageElement {
	const frame = set.frames[Math.floor(nowMs / frameDurationMs) % set.frames.length];
	return frame;
}
